import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';

const NewsUpdatesPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    api.get('/current-affairs?limit=20')
      .then(res => { if (res.data.success) setArticles(res.data.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const categories = [
    { key: 'all', label: '📋 All Updates' },
    { key: 'cuet-ug', label: '📘 CUET UG' },
    { key: 'cuet-pg', label: '📗 CUET PG' },
    { key: 'net', label: '📙 UGC NET / JRF' },
    { key: 'dates', label: '📅 Exam Dates' },
    { key: 'results', label: '🏆 Results' },
  ];

  const sampleNews = [
    { id: 1, title: 'CUET UG 2026 Exam Date Announced – Check Full Schedule', category: 'cuet-ug', date: 'March 2026', badge: '🔥 Hot' },
    { id: 2, title: 'UGC NET Notification 2026 Released – Application Process Begins', category: 'net', date: 'March 2026', badge: '🆕 New' },
    { id: 3, title: 'CUET PG Syllabus Updated – Important Changes You Must Know', category: 'cuet-pg', date: 'February 2026', badge: '📢 Update' },
    { id: 4, title: 'UGC NET Result Declared – Check Your Score Now', category: 'results', date: 'February 2026', badge: '🏆 Result' },
    { id: 5, title: 'CUET UG Application Form 2026 – Last Date Extended', category: 'cuet-ug', date: 'January 2026', badge: '⚠️ Alert' },
    { id: 6, title: 'CUET Counselling 2025 Schedule Released by NTA', category: 'cuet-ug', date: 'January 2026', badge: '📋 Update' },
  ];

  const displayItems = articles.length > 0 ? articles : sampleNews;
  const filtered = activeCategory === 'all' ? displayItems : displayItems.filter(a => a.category === activeCategory);

  const faqs = [
    { q: 'How often are updates published?', a: 'We publish updates regularly as soon as official notifications are released by NTA.' },
    { q: 'Are these updates reliable?', a: 'Yes, all updates are verified and based on official NTA and government sources.' },
    { q: 'Can I get alerts for updates?', a: 'Yes, you can subscribe for WhatsApp or email alerts below.' },
    { q: 'Which exams are covered?', a: 'CUET UG, CUET PG, UGC NET/JRF, and other competitive entrance exams.' },
  ];

  return (
    <>
      <Helmet>
        <title>CUET, UGC NET & Exam News | Latest Updates, Notifications & Results</title>
        <meta name="description" content="Get latest CUET, UGC NET & exam updates. Stay informed with notifications, syllabus changes, results & important announcements." />
        <meta name="keywords" content="CUET news, UGC NET news, CUET notifications, exam updates, CUET result, NTA notifications" />
        <link rel="canonical" href="https://besst.in/news-updates" />
        <meta property="og:title" content="CUET, UGC NET & Exam News | Latest Updates, Notifications & Results" />
        <meta property="og:description" content="Get latest CUET, UGC NET & exam updates. Stay informed with notifications, syllabus changes, results & announcements." />
        <meta property="og:url" content="https://besst.in/news-updates" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="BESST" />
      </Helmet>
      <div className="min-h-screen pt-32 pb-20">
        {/* Hero */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="hero-badge mx-auto mb-6">📢 Latest Notifications</div>
            <h1 className="text-4xl lg:text-5xl font-extrabold gradient-text mb-6 leading-tight">
              CUET, UGC NET &amp; Exam News
            </h1>
            <p className="text-xl text-text-muted mb-4">Stay updated with the latest exam notifications, syllabus changes, results &amp; announcements</p>
            <p className="text-text-muted mb-8">⚡ Fast Updates &nbsp;•&nbsp; ✅ Verified Information &nbsp;•&nbsp; 📋 All in One Place</p>
          </div>
        </div>

        {/* Category Filters */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(c => (
              <button key={c.key} onClick={() => setActiveCategory(c.key)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${activeCategory === c.key ? 'bg-primary text-white' : 'bg-white/10 text-text-muted hover:bg-white/20'}`}>
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* News Grid */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          {loading ? (
            <div className="text-center py-12 text-text-muted">Loading updates...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((item, i) => (
                <div key={item.id || i} className="glass-card hover:border-primary/50 transition-colors cursor-pointer group">
                  {item.badge && (
                    <span className="inline-block px-2 py-0.5 bg-primary/20 text-primary rounded text-xs font-bold mb-3">{item.badge}</span>
                  )}
                  <h3 className="text-text-light font-semibold mb-3 group-hover:text-primary transition-colors leading-snug">
                    {item.title || item.Title}
                  </h3>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-text-muted text-xs">{item.date || item.PublishDate || 'Recent'}</span>
                    <span className="text-primary text-sm font-semibold">Read more →</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {filtered.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-text-muted">No updates in this category yet. Check back soon!</p>
            </div>
          )}
        </div>

        {/* Trending */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">🔥 Trending Right Now</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {['CUET Counselling Updates', 'CUET Cut-Off Trends', 'UGC NET Application Dates', 'Exam Pattern Changes', 'Seat Allotment Results', 'New University Affiliations'].map((t, i) => (
              <div key={t} className="flex items-center gap-3 p-3 glass-card hover:border-primary/50 transition-colors cursor-pointer">
                <span className="text-accent-gold font-bold text-sm w-6 text-center">#{i + 1}</span>
                <span className="text-text-light text-sm">{t}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Subscribe */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <div className="glass-card max-w-2xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-text-light mb-2">📩 Get Instant Updates</h2>
            <p className="text-text-muted text-sm mb-6">Don't miss any important exam update — subscribe now</p>
            {subscribed ? (
              <div className="py-4">
                <div className="text-4xl mb-2">🎉</div>
                <p className="text-green-400 font-semibold">You're subscribed! We'll notify you of new updates.</p>
              </div>
            ) : (
              <div className="flex gap-3 max-w-md mx-auto">
                <input type="email" placeholder="Your email address" value={email} onChange={e => setEmail(e.target.value)}
                  className="flex-1 px-4 py-3 bg-bg-secondary border border-glass-border rounded-xl text-text-light focus:outline-none focus:border-primary" />
                <button onClick={() => email && setSubscribed(true)} className="btn-primary px-6 py-3 font-bold rounded-xl whitespace-nowrap">
                  Subscribe
                </button>
              </div>
            )}
            <div className="flex gap-4 justify-center mt-4">
              <a href="https://wa.me/919999999999" target="_blank" rel="noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-xl text-sm font-semibold hover:bg-green-500/30 transition-colors">
                💬 WhatsApp Alerts
              </a>
            </div>
          </div>
        </div>

        {/* Cross-sell */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-6">Keep Preparing Along with Updates</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[{ label: 'Mock Tests', href: '/test-zone', icon: '🧪' }, { label: 'Study Materials', href: '/study-materials', icon: '📚' }, { label: 'PYQs', href: '/pyq-cuet', icon: '📄' }, { label: 'Counselling', href: '/cuet-counselling', icon: '🎓' }].map(l => (
              <Link key={l.label} to={l.href} className="glass-card text-center hover:border-primary/50 transition-colors p-4">
                <div className="text-3xl mb-2">{l.icon}</div>
                <div className="text-text-light text-sm font-semibold">{l.label}</div>
              </Link>
            ))}
          </div>
        </div>

        {/* FAQ */}
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 mb-16">
          <h2 className="text-2xl font-bold gradient-text text-center mb-8">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((f, i) => (
              <details key={i} className="glass-card">
                <summary className="font-semibold text-text-light cursor-pointer list-none flex justify-between items-center">{f.q} <span className="text-primary text-xl">+</span></summary>
                <p className="text-text-muted mt-3 text-sm">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 sm:px-8">
          <div className="glass-card text-center max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-text-light mb-3">Stay Updated &amp; Stay Ahead</h2>
            <p className="text-text-muted mb-6">Don't miss any important exam update — stay informed and prepare better.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => window.scrollTo(0,0)} className="btn-primary px-8 py-3 font-bold rounded-xl">📢 Check Latest Updates</button>
              <button onClick={() => document.querySelector('input[type="email"]')?.focus()} className="px-8 py-3 font-bold rounded-xl border border-glass-border text-text-light hover:bg-white/5 transition-colors">📩 Subscribe for Alerts</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewsUpdatesPage;
